import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, BarChart, Bar
} from 'recharts';
import {
  BarChart3, Activity, DollarSign, Zap, AlertCircle, Clock,
  RefreshCw, ChevronDown, ChevronRight, Eye, Copy, Check,
  Terminal, MessageSquare, Database, Search, Bot, Upload,
  ChevronLeft, ChevronRight as ChevronRightIcon, Layers, Package
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

// ── Session type metadata ─────────────────────────────────────────────────────
const TYPE_META = {
  mongo_agent: {
    label: 'Mongo Agent',
    icon: Database,
    color: '#06B6D4',
    bgCard: 'bg-cyan-950/60',
    border: 'border-cyan-800/60',
    text: 'text-cyan-300',
    dot: 'bg-cyan-400',
  },
  yiqi_import: {
    label: 'YIQI Import',
    icon: Upload,
    color: '#10B981',
    bgCard: 'bg-emerald-950/60',
    border: 'border-emerald-800/60',
    text: 'text-emerald-300',
    dot: 'bg-emerald-400',
  },
  chat: {
    label: 'Chat',
    icon: MessageSquare,
    color: '#8B5CF6',
    bgCard: 'bg-violet-950/60',
    border: 'border-violet-800/60',
    text: 'text-violet-300',
    dot: 'bg-violet-400',
  },
  vector_service: {
    label: 'Embeddings / RAG',
    icon: Search,
    color: '#F59E0B',
    bgCard: 'bg-amber-950/60',
    border: 'border-amber-800/60',
    text: 'text-amber-300',
    dot: 'bg-amber-400',
  },
  embed: {
    label: 'Embeddings',
    icon: Search,
    color: '#F59E0B',
    bgCard: 'bg-amber-950/60',
    border: 'border-amber-800/60',
    text: 'text-amber-300',
    dot: 'bg-amber-400',
  },
  system: {
    label: 'Sistema',
    icon: Bot,
    color: '#6B7280',
    bgCard: 'bg-gray-900',
    border: 'border-gray-700',
    text: 'text-gray-400',
    dot: 'bg-gray-500',
  },
  legacy: {
    label: 'Histórico',
    icon: Package,
    color: '#6B7280',
    bgCard: 'bg-gray-900',
    border: 'border-gray-700',
    text: 'text-gray-400',
    dot: 'bg-gray-500',
  },
};

function getTypeMeta(type) {
  return TYPE_META[type] || {
    label: type || 'Desconocido',
    icon: Terminal,
    color: '#64748B',
    bgCard: 'bg-slate-900',
    border: 'border-slate-700',
    text:   'text-slate-400',
    dot:    'bg-slate-500',
  };
}


// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt      = (n) => (n || 0).toLocaleString('es-AR');
const fmtCost  = (v) => `$${(v || 0).toFixed(5)}`;
const fmtTime  = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('es-AR', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};
const fmtDur   = (start, end) => {
  if (!start || !end) return null;
  const ms = new Date(end) - new Date(start);
  if (ms <= 0) return null;
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}min`;
};

// ── Prompt/Response modal ────────────────────────────────────────────────────
const PromptModal = ({ call, onClose }) => {
  const [copied, setCopied] = useState(false);

  const cleanText = (raw) => {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      const cleaned = parsed.map(msg => {
        if (msg.content && typeof msg.content === 'string') {
          try { return { ...msg, content: JSON.parse(msg.content) }; } catch { return msg; }
        }
        return msg;
      });
      return JSON.stringify(cleaned, null, 2)
        .replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n').replace(/\\r/g, '\n').replace(/\\"/g, '"');
    } catch { return raw; }
  };

  const promptText = cleanText(call?.prompt);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex justify-between items-center">
            <div>
              <p className="text-sm font-semibold text-white">{call?.endpoint}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {fmtTime(call?.timestamp)} · {call?.model} · {fmt(call?.tokens_total)} tokens · {fmtCost(call?.cost_estimated)}
              </p>
            </div>
            {promptText && (
              <button
                onClick={() => { navigator.clipboard.writeText(promptText); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-xs transition-colors mr-6"
              >
                {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                {copied ? 'Copiado' : 'Copiar'}
              </button>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto mt-3 space-y-3">
          {promptText && (
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-600 mb-1">Prompt enviado</p>
              <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap break-words bg-black/30 rounded-lg p-4 border border-gray-800">{promptText}</pre>
            </div>
          )}
          {call?.response && (
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-600 mb-1">Respuesta</p>
              <pre className="text-xs text-emerald-300 font-mono whitespace-pre-wrap break-words bg-black/30 rounded-lg p-4 border border-gray-800">{call.response}</pre>
            </div>
          )}
          {call?.error && (
            <div>
              <p className="text-xs uppercase tracking-wider text-red-500 mb-1">Error</p>
              <pre className="text-xs text-red-300 font-mono whitespace-pre-wrap break-words bg-red-950/30 rounded-lg p-4 border border-red-900">{call.error}</pre>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Single OpenAI call row (inside expanded execution) ────────────────────────
const CallRow = ({ call, index }) => {
  const [open, setOpen] = useState(false);
  const isError = !call.success;

  return (
    <>
      <div
        onClick={() => setOpen(true)}
        className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs cursor-pointer transition-colors ${
          isError
            ? 'bg-red-900/20 hover:bg-red-900/30 border border-red-800/40'
            : 'bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700/30'
        }`}
      >
        <span className="text-gray-600 font-mono w-4 shrink-0">#{index + 1}</span>
        <span className={`font-mono px-1.5 py-0.5 rounded text-xs shrink-0 ${isError ? 'bg-red-900/50 text-red-400' : 'bg-gray-700 text-cyan-400'}`}>
          {call.endpoint}
        </span>
        <span className="text-gray-600 whitespace-nowrap">{fmtTime(call.timestamp)}</span>
        <div className="flex-1" />
        {call.model && <span className="text-gray-600 hidden md:block">{call.model}</span>}
        <span className="text-gray-400 flex items-center gap-1">
          <Zap className="w-3 h-3 text-yellow-500" />{fmt(call.tokens_total)}
        </span>
        <span className="text-green-400 font-mono w-20 text-right">{fmtCost(call.cost_estimated)}</span>
        <span className="text-gray-600 w-14 text-right">{call.latency_ms}ms</span>
        {isError
          ? <AlertCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />
          : <span className="text-green-400 shrink-0">✓</span>
        }
        <Eye className="w-3.5 h-3.5 text-gray-600 hover:text-cyan-400 shrink-0" />
      </div>
      {open && <PromptModal call={call} onClose={() => setOpen(false)} />}
    </>
  );
};

// ── Execution row (level 2: one execution within a type) ──────────────────────
const ExecutionRow = ({ execution, index, typeMeta }) => {
  const [expanded, setExpanded] = useState(false);
  const dur = fmtDur(execution.started_at, execution.ended_at);

  return (
    <div className="rounded-lg border border-gray-700/50 overflow-hidden">
      {/* Execution header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-gray-800/60 hover:bg-gray-700/60 transition-colors text-left"
      >
        {expanded
          ? <ChevronDown className="w-3.5 h-3.5 text-gray-500 shrink-0" />
          : <ChevronRight className="w-3.5 h-3.5 text-gray-500 shrink-0" />
        }

        {/* Dot indicator */}
        <span className={`w-2 h-2 rounded-full shrink-0 ${typeMeta.dot}`} />

        {/* Timestamp */}
        <span className="text-gray-300 text-sm whitespace-nowrap">
          {fmtTime(execution.started_at)}
        </span>

        {/* Label (UUID for chat) */}
        {execution.label && (
          <span className="text-gray-600 text-xs font-mono hidden sm:block">{execution.label}</span>
        )}

        {/* Duration */}
        {dur && (
          <span className="text-gray-600 text-xs flex items-center gap-1 hidden md:flex">
            <Clock className="w-3 h-3" />{dur}
          </span>
        )}

        <div className="flex-1" />

        {/* Calls count */}
        <span className="text-gray-400 text-xs flex items-center gap-1">
          <Activity className="w-3 h-3" />
          {execution.total_calls} llamadas
        </span>

        {/* Tokens */}
        <span className="text-gray-400 text-xs flex items-center gap-1 ml-4">
          <Zap className="w-3 h-3 text-yellow-500" />
          {fmt(execution.total_tokens)}
        </span>

        {/* Cost */}
        <span className="text-green-400 font-mono text-xs ml-4 w-20 text-right">
          {fmtCost(execution.total_cost)}
        </span>

        {/* Error badge */}
        {execution.errors > 0 && (
          <span className="ml-2 text-xs bg-red-900/50 text-red-400 border border-red-800 px-1.5 py-0.5 rounded-full flex items-center gap-1">
            <AlertCircle className="w-2.5 h-2.5" />{execution.errors}
          </span>
        )}
      </button>

      {/* Individual calls */}
      {expanded && (
        <div className="border-t border-gray-700/30 bg-gray-900/60 p-3 space-y-1.5">
          {execution.calls.slice().reverse().map((call, i) => (
            <CallRow key={call.id || i} call={call} index={execution.calls.length - i} />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Session type card (level 1: mongo_agent, chat, vector_service…) ───────────
const SessionTypeCard = ({ sessionType }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = getTypeMeta(sessionType.type);
  const Icon = meta.icon;

  return (
    <div className={`rounded-xl border ${meta.border} overflow-hidden`}>
      {/* Type header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center gap-4 px-5 py-4 ${meta.bgCard} hover:brightness-110 transition-all text-left`}
      >
        {expanded
          ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
          : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
        }

        {/* Icon + label */}
        <div className="flex items-center gap-2 min-w-[180px]">
          <Icon className="w-5 h-5 shrink-0" style={{ color: meta.color }} />
          <span className={`font-bold text-sm ${meta.text}`}>{meta.label}</span>
          <span className="text-gray-600 text-xs font-mono hidden md:block">({sessionType.type})</span>
        </div>

        {/* Most recent */}
        <span className="text-gray-500 text-xs whitespace-nowrap hidden sm:block">
          último: {fmtTime(sessionType.most_recent)}
        </span>

        <div className="flex-1" />

        {/* Executions count */}
        <div className="flex items-center gap-1 text-gray-400 text-sm">
          <Layers className="w-3.5 h-3.5" />
          <span>{sessionType.total_executions}</span>
          <span className="text-gray-600 text-xs">ejec.</span>
        </div>

        {/* Total calls */}
        <div className="flex items-center gap-1 text-gray-400 text-sm ml-5">
          <Activity className="w-3.5 h-3.5" />
          <span>{sessionType.total_calls}</span>
          <span className="text-gray-600 text-xs">llamadas</span>
        </div>

        {/* Tokens */}
        <div className="flex items-center gap-1 text-gray-300 text-sm ml-5">
          <Zap className="w-3.5 h-3.5 text-yellow-500" />
          <span>{fmt(sessionType.total_tokens)}</span>
        </div>

        {/* Cost */}
        <span className="text-green-400 font-mono font-semibold text-sm ml-5 w-24 text-right">
          {fmtCost(sessionType.total_cost)}
        </span>

        {/* Errors badge */}
        {sessionType.errors > 0 && (
          <span className="ml-3 flex items-center gap-1 text-xs bg-red-900/50 text-red-400 border border-red-800 px-2 py-0.5 rounded-full">
            <AlertCircle className="w-3 h-3" />{sessionType.errors} err
          </span>
        )}
      </button>

      {/* Executions list */}
      {expanded && (
        <div className="border-t border-gray-700/30 bg-gray-900/40 p-4 space-y-2">
          {sessionType.executions.map((exec, i) => (
            <ExecutionRow key={exec.started_at + i} execution={exec} index={i} typeMeta={meta} />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const ConsumoPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data,  setData]  = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [dateTo,    setDateTo]    = useState(() => new Date().toISOString().split('T')[0]);
  const [typeFilter, setTypeFilter] = useState('');
  const [model,      setModel]      = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchData = useCallback(async () => {
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (dateFrom)    params.append('date_from',  new Date(dateFrom).toISOString());
      if (dateTo)      params.append('date_to',    new Date(dateTo + 'T23:59:59').toISOString());
      if (typeFilter)  params.append('session_id', typeFilter);
      if (model)       params.append('model',      model);

      const [sessRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/consumo/sessions?${params}`, { headers }),
        fetch(`${API_URL}/api/admin/consumo/stats`, { headers }),
      ]);
      if (!sessRes.ok)  throw new Error(`Sessions: ${sessRes.status}`);
      if (!statsRes.ok) throw new Error(`Stats: ${statsRes.status}`);

      setData(await sessRes.json());
      setStats(await statsRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, dateFrom, dateTo, typeFilter, model]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const summary    = stats?.summary    || {};
  const dailyCalls = summary.daily_calls || [];
  const dailyCost  = summary.daily_cost  || [];

  // Client-side type filter (since backend uses session_id exact match)
  const sessionTypes = (data?.session_types || []).filter(s =>
    !typeFilter || s.type === typeFilter
  );

  return (
    <div className="text-gray-200">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex justify-between items-center bg-[#1A1D27] p-6 rounded-xl border border-gray-800 shadow-xl mb-6">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent flex items-center gap-3">
              <BarChart3 className="w-7 h-7 text-[#00a1df]" />
              Consumo API
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Métricas de uso de OpenAI: Tokens, Costos y Trazabilidad de ejecuciones
            </p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-500 text-white px-5 py-2.5 rounded-lg text-xs font-black transition-all shadow-lg shadow-cyan-900/20 active:scale-95"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            REFRESCAR DATOS
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-900/40 border border-red-700 rounded-lg p-4 text-red-300 flex items-center gap-2 text-sm">
            <AlertCircle className="w-5 h-5 shrink-0" />{error}
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Llamadas',  val: fmt(summary.total_calls),                         Icon: Activity,   c: 'text-cyan-400',   bg: 'bg-cyan-950/50',    border: 'border-cyan-800/50' },
            { label: 'Total Tokens',    val: fmt(summary.total_tokens),                        Icon: Zap,        c: 'text-yellow-400', bg: 'bg-amber-950/50',   border: 'border-amber-800/50' },
            { label: 'Costo Estimado',  val: fmtCost(summary.total_cost),                      Icon: DollarSign, c: 'text-green-400',  bg: 'bg-emerald-950/50', border: 'border-emerald-800/50' },
            { label: 'Latencia Prom.',  val: `${(summary.avg_latency_ms || 0).toFixed(0)} ms`, Icon: Clock,      c: 'text-purple-400', bg: 'bg-purple-950/50',  border: 'border-purple-800/50' },
          ].map(({ label, val, Icon, c, bg, border }) => (
            <div key={label} className={`rounded-xl border ${border} ${bg} p-4 flex items-center justify-between`}>
              <div>
                <p className="text-gray-500 text-xs mb-1">{label}</p>
                <p className={`text-xl font-bold ${c}`}>{val}</p>
              </div>
              <Icon className={`w-7 h-7 ${c} opacity-60`} />
            </div>
          ))}
        </div>

        {/* Mini charts */}
        {dailyCalls.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-4">
              <p className="text-xs text-gray-500 mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4 text-cyan-500" /> Llamadas por día
              </p>
              <ResponsiveContainer width="100%" height={140}>
                <LineChart data={dailyCalls}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis dataKey="date" stroke="#374151" tick={{ fontSize: 10 }} tickFormatter={v => v?.slice(5) || ''} />
                  <YAxis stroke="#374151" tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#111827', border: '1px solid #1f2937' }} />
                  <Line type="monotone" dataKey="value" stroke="#06B6D4" strokeWidth={2} dot={false} name="Llamadas" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-4">
              <p className="text-xs text-gray-500 mb-3 flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-green-500" /> Costo por día
              </p>
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={dailyCost}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis dataKey="date" stroke="#374151" tick={{ fontSize: 10 }} tickFormatter={v => v?.slice(5) || ''} />
                  <YAxis stroke="#374151" tick={{ fontSize: 10 }} tickFormatter={v => `$${v}`} />
                  <Tooltip contentStyle={{ backgroundColor: '#111827', border: '1px solid #1f2937' }} formatter={v => fmtCost(v)} />
                  <Bar dataKey="value" fill="#10B981" radius={[3, 3, 0, 0]} name="Costo" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Desde</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Hasta</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Tipo de sesión</label>
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm">
                <option value="">Todas</option>
                <option value="mongo_agent">Mongo Agent</option>
                <option value="chat">Chat</option>
                <option value="vector_service">Embeddings / RAG</option>
                <option value="system">Sistema</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Modelo</label>
              <select value={model} onChange={e => setModel(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm">
                <option value="">Todos</option>
                <option value="gpt-4o">GPT-4o</option>
                <option value="gpt-4o-mini">GPT-4o-mini</option>
                <option value="text-embedding-3-small">Embedding 3 Small</option>
              </select>
            </div>
          </div>
        </div>

        {/* Session types list */}
        <div className="space-y-4">
          <h2 className="text-white font-semibold flex items-center gap-2">
            <Terminal className="w-4 h-4 text-cyan-500" />
            Tipos de sesión
            {data && (
              <span className="text-gray-600 text-sm font-normal">
                · {sessionTypes.length} tipos · {data.session_types?.reduce((a, s) => a + s.total_executions, 0)} ejecuciones
              </span>
            )}
          </h2>

          {loading && !data ? (
            <div className="flex justify-center items-center h-48">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-cyan-500" />
            </div>
          ) : sessionTypes.length === 0 ? (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-12 text-center text-gray-600 text-sm">
              Sin ejecuciones en el período seleccionado
            </div>
          ) : (
            sessionTypes.map(st => (
              <SessionTypeCard key={st.type} sessionType={st} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsumoPage;
